import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { buildCurrentUserStakeholder } from "./my-relationships/myRelationshipsDataAdapter";
import MyRelationshipsTab from "./my-relationships/MyRelationshipsTab";
import {
  EXPLORE_ALL_RELATIONSHIPS_COMING_SOON_MSG,
  EXPLORE_ALL_RELATIONSHIPS_ENABLED,
} from "./my-relationships/relationshipTabAccess";
import RelationshipAccountsLoader from "./RelationshipAccountsLoader";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchMyRelationshipAccounts,
  fetchTeamMemberAccountsDirect,
  refreshMyRelationshipUserMappings,
} from "../../Store/Reducers/RelationshipSlice";
import type {
  RelationshipAccountSummary,
  RelationshipAssessmentStakeholder,
} from "../../Store/Reducers/RelationshipSlice";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import { toast } from "../../Utils/Helper/toast";

import "./account-relationships/relationship-prototype-skin.css";

interface MyRelationshipsShellProps {
  refreshKey?: number;
  onNavigateToAccountRelationships?: (
    account: RelationshipAccountSummary
  ) => void;
  onNavigateToPortfolioOverview?: () => void;
  overrideUserId?: string;
  overrideUserName?: string;
  portfolioOwnerFirstName?: string;
  teamsRelationshipToggle?: boolean;
  onTeamsRelationshipToggle?: (checked: boolean) => void;
  teamsSelectedUserName?: string;
  teamMemberAccountIds?: string[];
  teamsSelectedUserId?: string;
  teamsSelectedUserInfo?: RelationshipAssessmentStakeholder | null;
  onOpenTeamModal?: () => void;
}

const MyRelationshipsShell: React.FC<MyRelationshipsShellProps> = ({
  refreshKey = 0,
  onNavigateToAccountRelationships,
  onNavigateToPortfolioOverview,
  overrideUserId,
  overrideUserName,
  portfolioOwnerFirstName = "",
  teamsRelationshipToggle = false,
  onTeamsRelationshipToggle,
  teamsSelectedUserName,
  teamMemberAccountIds = [],
  teamsSelectedUserId,
  teamsSelectedUserInfo,
  onOpenTeamModal,
}) => {
  const dispatch = useAppDispatch();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const loggedInUserId = String(
    loggedInUser?.user_id || loggedInUser?.userId || loggedInUser?.id || ""
  ).trim();
  const userId = overrideUserId || loggedInUserId;
  const lastRefreshKeyRef = useRef(0);

  const [viewMyGraph, setViewMyGraph] = useState(false);
  const [teamMemberGraphAccounts, setTeamMemberGraphAccounts] = useState<
    RelationshipAccountSummary[]
  >([]);
  const [teamMemberGraphWorkspaceUser, setTeamMemberGraphWorkspaceUser] =
    useState<Record<string, unknown> | null>(null);

  const loadTeamMemberGraph = useCallback(async () => {
    if (!teamsRelationshipToggle || !teamsSelectedUserId) {
      setTeamMemberGraphAccounts([]);
      setTeamMemberGraphWorkspaceUser(null);
      return;
    }
    try {
      const result = await fetchTeamMemberAccountsDirect(teamsSelectedUserId);
      setTeamMemberGraphAccounts(result.accounts ?? []);
      setTeamMemberGraphWorkspaceUser(result.workspaceUser ?? null);
    } catch {
      setTeamMemberGraphAccounts([]);
      setTeamMemberGraphWorkspaceUser(null);
    }
  }, [teamsRelationshipToggle, teamsSelectedUserId]);

  useEffect(() => {
    setViewMyGraph(false);
    if (!teamsRelationshipToggle || !teamsSelectedUserId) {
      setTeamMemberGraphAccounts([]);
      setTeamMemberGraphWorkspaceUser(null);
    }
  }, [teamsRelationshipToggle, teamsSelectedUserId]);

  useEffect(() => {
    void loadTeamMemberGraph();
  }, [loadTeamMemberGraph]);

  const {
    myRelationshipAccounts,
    myRelationshipAccountsLoading,
    myRelationshipAccountsUserId,
    myRelationshipWorkspaceUser,
    myRelationshipMappingsLoading,
    myRelationshipAccountsStale,
    error: relationshipError,
  } = useAppSelector(state => state.relationship);

  useEffect(() => {
    if (!userId) return;

    const refreshKeyBumped = refreshKey > lastRefreshKeyRef.current;
    const shouldForce = refreshKeyBumped || myRelationshipAccountsStale;

    if (shouldForce) {
      if (refreshKeyBumped) {
        lastRefreshKeyRef.current = refreshKey;
      }
      dispatch(fetchMyRelationshipAccounts({ userId, force: true }));
      return;
    }

    if (
      myRelationshipAccountsUserId === userId &&
      myRelationshipAccounts.length > 0
    ) {
      return;
    }

    dispatch(fetchMyRelationshipAccounts(userId));
  }, [
    dispatch,
    myRelationshipAccounts.length,
    myRelationshipAccountsUserId,
    myRelationshipAccountsStale,
    refreshKey,
    userId,
  ]);

  const currentUserStakeholder = useMemo(
    () =>
      buildCurrentUserStakeholder(
        myRelationshipWorkspaceUser || {
          user_id: userId,
          user_name: overrideUserId
            ? overrideUserName || undefined
            : loggedInUser?.name || loggedInUser?.user_name,
          title: overrideUserId ? undefined : loggedInUser?.title,
          role: overrideUserId ? undefined : loggedInUser?.role,
          email: overrideUserId ? undefined : loggedInUser?.email,
        }
      ),
    [
      loggedInUser,
      myRelationshipWorkspaceUser,
      overrideUserId,
      overrideUserName,
      userId,
    ]
  );

  const teamMemberGraphCenterStakeholder = useMemo(
    () =>
      teamMemberGraphWorkspaceUser
        ? buildCurrentUserStakeholder(teamMemberGraphWorkspaceUser)
        : null,
    [teamMemberGraphWorkspaceUser]
  );

  const accounts = useMemo(
    () =>
      myRelationshipAccounts.map(account => ({
        ...account,
        currentUserStakeholder:
          account.currentUserStakeholder || currentUserStakeholder,
      })),
    [currentUserStakeholder, myRelationshipAccounts]
  );

  const accountsMatchUser =
    !myRelationshipAccounts.length || myRelationshipAccountsUserId === userId;
  const displayAccounts = accountsMatchUser ? accounts : [];

  const handleSelectAccount = useCallback(
    (accountId: string) => {
      const account = displayAccounts.find(item => item.id === accountId);
      if (account) {
        onNavigateToAccountRelationships?.(account);
      }
    },
    [displayAccounts, onNavigateToAccountRelationships]
  );

  const handleExploreAll = useCallback(() => {
    if (!EXPLORE_ALL_RELATIONSHIPS_ENABLED) {
      toast.info(EXPLORE_ALL_RELATIONSHIPS_COMING_SOON_MSG);
      return;
    }
    onNavigateToPortfolioOverview?.();
  }, [onNavigateToPortfolioOverview]);

  const handleRefreshMappings = useCallback(async () => {
    if (!userId) return;
    await dispatch(refreshMyRelationshipUserMappings({ userId }));
    // Team Relationship graph is local state (selected teammate) — refresh it too.
    if (teamsRelationshipToggle && teamsSelectedUserId) {
      await loadTeamMemberGraph();
    }
  }, [
    dispatch,
    loadTeamMemberGraph,
    teamsRelationshipToggle,
    teamsSelectedUserId,
    userId,
  ]);

  const loading =
    !accountsMatchUser ||
    ((myRelationshipAccountsLoading || myRelationshipMappingsLoading) &&
      !displayAccounts.length);

  if (!userId) {
    return (
      <div className="rl-prototype-skin flex min-h-[320px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white p-8 text-sm text-gray-500">
        Sign in to view your relationship portfolio.
      </div>
    );
  }

  if (loading) {
    return <RelationshipAccountsLoader label="Loading your relationships…" />;
  }

  if (relationshipError && !displayAccounts.length) {
    return (
      <div className="rl-prototype-skin rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
        {relationshipError}
      </div>
    );
  }

  return (
    <MyRelationshipsTab
      accounts={displayAccounts}
      currentUserStakeholder={currentUserStakeholder}
      executiveId={userId}
      loggedInUserId={loggedInUserId}
      portfolioOwnerFirstName={portfolioOwnerFirstName}
      onSelectAccount={handleSelectAccount}
      onExploreAllRelationships={handleExploreAll}
      onRefreshMappings={handleRefreshMappings}
      teamsRelationshipToggle={teamsRelationshipToggle}
      onTeamsRelationshipToggle={onTeamsRelationshipToggle}
      teamsSelectedUserName={teamsSelectedUserName}
      teamsSelectedUserInfo={teamsSelectedUserInfo as never}
      teamMemberAccountIds={teamMemberAccountIds}
      teamsSelectedUserId={teamsSelectedUserId}
      onOpenTeamModal={onOpenTeamModal}
      viewMyGraph={viewMyGraph}
      onToggleViewMyGraph={setViewMyGraph}
      teamMemberGraphAccounts={teamMemberGraphAccounts as never}
      teamMemberGraphCenterStakeholder={
        teamMemberGraphCenterStakeholder as never
      }
    />
  );
};

export default MyRelationshipsShell;
